import { Calendar, MapPin, Award, LucideIcon } from "lucide-react";
import Link from "next/link";
import React from "react";
import { Button } from "shared/components/ui/button";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "shared/components/ui/card";

import { CardColorScheme, getCardColorScheme } from "@/lib/constants/colors";
import { formatDate, formatTimeRange } from "@/lib/utils/dateUtils";
import { Event } from "@/types/events";

interface InfoCardProps {
  // Core content props
  title: string;
  subtitle: string;
  eventDetails: Event;

  // Optional styling props
  colorScheme?: CardColorScheme;
  icon?: LucideIcon;
  iconSize?: number;

  // Optional link props
  detailsUrl?: string;
  detailsLinkText?: string;
}

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  subtitle,
  eventDetails,
  colorScheme = "scout-blue",
  icon: Icon = Award,
  iconSize = 5,
  detailsUrl,
  detailsLinkText = "View Details",
}) => {
  // Get color scheme from our shared constants
  const color = getCardColorScheme(colorScheme);

  // Format date display
  const displayDate = formatDate(eventDetails.startDate, "medium");

  // Format time display
  const timeDisplay =
    formatTimeRange(eventDetails.startTime, eventDetails.endTime) ||
    "Time not specified";

  return (
    <Card className={`${color.border} shadow-sm overflow-hidden`}>
      <div
        className={`bg-gradient-to-r ${color.gradientFrom} ${color.gradientTo} border-b ${color.borderB}`}
      >
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className={color.title}>{title}</CardTitle>
            <Icon className={`h-${iconSize} w-${iconSize} ${color.icon}`} />
          </div>
          <CardDescription>{subtitle}</CardDescription>
        </CardHeader>
      </div>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Calendar className={`h-5 w-5 ${color.iconColor} mt-1`} />
            <div>
              <p className={`text-sm font-medium ${color.labelText}`}>
                DATE & TIME
              </p>
              <p className="font-medium">{displayDate}</p>
              <p>{timeDisplay}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <MapPin className={`h-5 w-5 ${color.iconColor} mt-1`} />
            <div>
              <p className={`text-sm font-medium ${color.labelText}`}>
                LOCATION
              </p>
              <p>{eventDetails.location}</p>
            </div>
          </div>
          <div>
            <p className="text-gray-700">
              {typeof eventDetails.description === "string"
                ? eventDetails.description
                    .replace(/&nbsp;/g, " ")
                    .replace(/&amp;/g, "&")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">")
                    .replace(/&quot;/g, '"')
                : ""}
            </p>
          </div>
          {detailsUrl && (
            <div className="pt-4 border-t">
              <Button asChild size="sm" variant="default">
                <Link href={detailsUrl}>{detailsLinkText}</Link>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InfoCard;
